/**
 * DOM bindings: controls → state, state → controls, plus notices, readouts, toasts, the
 * settings sheet, the "How it works" drawer and export/share.
 */
import { MODELS } from '../geometry/models';
import { PRESETS } from '../geometry/triangle-group';
import { PAINT } from '../render/palette';
import { type App, type AppCallbacks, type NoticeSpec } from './app';
import { fmt, fmtDeg } from './measure';
import type { AppState, Tool } from './state';
import { describeColourGroup } from './describe';

const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document): T => {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`Missing element ${sel}`);
  return el;
};
const WIDE = window.matchMedia('(min-width: 1100px) and (min-height: 640px)');

const $$ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  Array.from(root.querySelectorAll<T>(sel));

export class UI {
  app!: App;
  private toastTimer = 0;
  private hintTimer = 0;
  private lastFocus: HTMLElement | null = null;
  private drawerLoaded = false;
  readonly appEl = $('#app');

  callbacks(): AppCallbacks {
    return {
      onState: (s, prev) => this.syncState(s, prev),
      onHistory: (u, r) => {
        $<HTMLButtonElement>('#undo').disabled = !u;
        $<HTMLButtonElement>('#redo').disabled = !r;
      },
      onGroupInfo: () => this.syncGroup(),
      onReadout: () => this.renderReadout(),
      onNotice: (n) => this.renderNotice(n),
      onToast: (m, k) => this.toast(m, k),
      onHintDone: () => this.hideHint(),
    };
  }

  bind(app: App): void {
    this.app = app;
    const store = app.store;

    $$<HTMLButtonElement>('.tool[data-tool]').forEach((b) =>
      b.addEventListener('click', () => store.set({ tool: b.dataset.tool as Tool })),
    );
    $$<HTMLInputElement>('input[name="paint"]').forEach((r) =>
      r.addEventListener('change', () => {
        if (r.checked) {
          const t = store.get().tool;
          store.set({
            role: Number(r.value) as 0 | 1 | 2 | 3,
            tool: t === 'brush' || t === 'eraser' ? 'brush' : t,
          });
        }
      }),
    );
    const size = $<HTMLInputElement>('#brush-size');
    size.addEventListener('input', () => store.set({ size: Number(size.value) }));
    $('#undo').addEventListener('click', () => app.undo());
    $('#redo').addEventListener('click', () => app.redo());
    $('#clear').addEventListener('click', () => app.clearMotif());

    // settings sheet (compact layouts)
    const toggle = $<HTMLButtonElement>('#settings-toggle');
    toggle.addEventListener('click', () => this.setSheet(this.appEl.dataset.sheet !== 'open'));
    $('#panel-close').addEventListener('click', () => this.setSheet(false));
    $('#scrim').addEventListener('click', () => this.setSheet(false));

    // tiling
    $$<HTMLButtonElement>('[data-preset]').forEach((b) =>
      b.addEventListener('click', () => {
        const [p, q] = b.dataset.preset!.split(',').map(Number);
        store.set({ p, q, r: 2 });
      }),
    );
    const form = $<HTMLFormElement>('#custom-group');
    const inputs = ['#order-p', '#order-q', '#order-r'].map((s) => $<HTMLInputElement>(s));
    inputs.forEach((i) => i.addEventListener('input', () => this.checkCustom()));
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const [p, q, r] = inputs.map((i) => Number(i.value));
      const check = (app.constructor as typeof App).checkTriple(p, q, r);
      if (check.ok) {
        store.set({ p, q, r });
        this.toast(`Now tiling with the (${p}, ${q}, ${r}) triangle group`);
      }
    });

    $$<HTMLInputElement>('input[name="symmetry"]').forEach((r) =>
      r.addEventListener(
        'change',
        () => r.checked && store.set({ rotation: r.value === 'rotation' }),
      ),
    );
    $$<HTMLInputElement>('input[name="scheme"]').forEach((r) =>
      r.addEventListener(
        'change',
        () => r.checked && store.set({ scheme: r.value as AppState['scheme'] }),
      ),
    );
    $$<HTMLInputElement>('input[name="model"]').forEach((r) =>
      r.addEventListener(
        'change',
        () => r.checked && store.set({ model: r.value as AppState['model'] }),
      ),
    );
    $$<HTMLInputElement>('input[name="grid"]').forEach((r) =>
      r.addEventListener(
        'change',
        () => r.checked && store.set({ grid: r.value as AppState['grid'] }),
      ),
    );

    $('#drift').addEventListener('click', () => store.set({ drift: !store.get().drift }));
    $('#home').addEventListener('click', () => app.goHome());

    $('#export').addEventListener('click', () => void this.doExport());
    $('#share').addEventListener('click', () => void this.doShare());

    $('#how-open').addEventListener('click', () => void this.openDrawer());
    $('#drawer-close').addEventListener('click', () => this.closeDrawer());
    $('#keys-open').addEventListener('click', () => this.showKeys());

    WIDE.addEventListener('change', () => this.renderReadout());

    this.syncState(store.get(), null);
    this.syncGroup();
    this.renderReadout();
    if (!app.hintDone) this.showHint();
  }

  /* ------------------------------------------------------------------ state → DOM */

  syncState(s: AppState, prev: AppState | null): void {
    $$<HTMLButtonElement>('.tool[data-tool]').forEach((b) =>
      b.setAttribute('aria-pressed', String(b.dataset.tool === s.tool)),
    );
    $$<HTMLInputElement>('input[name="paint"]').forEach(
      (r) => (r.checked = Number(r.value) === s.role),
    );
    $<HTMLInputElement>('#brush-size').value = String(s.size);
    const dot = $('.size-dot');
    const px = 6 + (s.size / 100) * 18;
    dot.style.setProperty('--dot', `${px.toFixed(1)}px`);
    dot.style.setProperty('--dot-color', s.tool === 'eraser' ? 'transparent' : PAINT[s.role].hex);
    dot.style.boxShadow = s.tool === 'eraser' ? '0 0 0 1.5px rgba(239,230,210,.7) inset' : '';
    $$<HTMLButtonElement>('[data-preset]').forEach((b) => {
      const [p, q] = b.dataset.preset!.split(',').map(Number);
      b.setAttribute('aria-pressed', String(p === s.p && q === s.q && s.r === 2));
    });
    const setRadio = (name: string, value: string) =>
      $$<HTMLInputElement>(`input[name="${name}"]`).forEach((r) => (r.checked = r.value === value));
    setRadio('symmetry', s.rotation ? 'rotation' : 'reflection');
    setRadio('scheme', s.scheme);
    setRadio('model', s.model);
    setRadio('grid', s.grid);
    const parity = $<HTMLInputElement>('input[name="scheme"][value="parity"]');
    parity.disabled = s.rotation;
    if (s.rotation && s.scheme === 'parity') this.app.store.set({ scheme: 'single' });
    const drift = $<HTMLButtonElement>('#drift');
    drift.setAttribute('aria-pressed', String(s.drift));
    drift.querySelector('use')!.setAttribute('href', s.drift ? '#i-pause' : '#i-play');
    $('#drift-label').textContent = s.drift ? 'Pause the drift' : 'Drift along horocycles';
    drift.title = s.drift ? 'Pause the drift' : 'Drift along horocycles';
    if (!prev || prev.p !== s.p || prev.q !== s.q || prev.r !== s.r) {
      $<HTMLInputElement>('#order-p').value = String(s.p);
      $<HTMLInputElement>('#order-q').value = String(s.q);
      $<HTMLInputElement>('#order-r').value = String(s.r);
      this.checkCustom();
    }
    if (!prev || prev.tool !== s.tool) {
      this.renderReadout();
      this.updateHintText();
    }
    if (!prev || prev.rotation !== s.rotation || prev.scheme !== s.scheme) this.syncGroup();
    if (prev && prev.model !== s.model) {
      const label = MODELS.find((m) => m.id === s.model)!.label;
      this.toast(`${label} model`);
    }
    this.updateStageLabel();
  }

  syncGroup(): void {
    const app = this.app;
    if (!app?.tg) return;
    const s = app.store.get();
    const { p, q, r } = s;
    const preset = r === 2 && PRESETS.some((x) => x.p === p && x.q === q);
    $('#tiling-note').textContent = preset
      ? `{${p},${q}} · ${tileWords(p, q)}`
      : `(${p}, ${q}, ${r}) triangle group`;
    $('#orb-refl').textContent = `*${p}${q}${r}`;
    $('#orb-rot').textContent = `${p}${q}${r}`;
    const classes = $<HTMLInputElement>('input[name="scheme"][value="classes"]');
    classes.disabled = !app.colouring;
    const help = $('#scheme-help');
    if (s.scheme === 'single') {
      help.textContent = s.rotation
        ? 'Every copy of the motif is painted alike. (Parity needs mirrors: switch to Reflections.)'
        : 'Every copy of the motif is painted alike.';
    } else if (s.scheme === 'parity') {
      help.textContent =
        'Copies made by an odd number of reflections swap paper and ink — the kaleidoscope’s chequerboard.';
    } else if (app.colouring) {
      const c = app.colouring;
      help.textContent = `${c.k} colours; every rotation of the pattern permutes them (${describeColourGroup(c)}). The ${p} copies around each tile centre all differ.`;
    } else {
      help.textContent = 'No rotation colouring with at most 8 colours exists for this group.';
    }
    if (s.rotation && !app.colouring && s.scheme !== 'single')
      help.textContent = 'Every copy of the motif is painted alike.';
    $('#editor-note').textContent = s.rotation
      ? 'two triangles · rotations'
      : 'one triangle · mirrors';
    this.checkCustom();
    this.updateStageLabel();
  }

  private checkCustom(): void {
    if (!this.app) return;
    const [p, q, r] = ['#order-p', '#order-q', '#order-r'].map((sel) =>
      Number($<HTMLInputElement>(sel).value),
    );
    const status = $('#group-status');
    const apply = $<HTMLButtonElement>('#apply-group');
    const s = this.app.store.get();
    const res = (this.app.constructor as typeof App).checkTriple(p, q, r);
    const same = p === s.p && q === s.q && r === s.r;
    if (!res.ok && res.reason === 'range') {
      status.dataset.kind = 'invalid';
      status.textContent = 'Orders must be whole numbers from 2 to 24.';
      apply.disabled = true;
      return;
    }
    const sum = res.cls!.angleSumDeg;
    if (!res.ok) {
      status.dataset.kind = 'invalid';
      if (res.reason === 'euclidean') {
        status.innerHTML = `Angles π/${p} + π/${q} + π/${r} sum to exactly 180°: a <em>Euclidean</em> triangle. It tiles the flat plane, not the hyperbolic one.`;
      } else {
        status.innerHTML = `Angles sum to ${sum.toFixed(1)}° &gt; 180°: a <em>spherical</em> triangle. ${res.cls!.sphericalCount} copies close up into a sphere — a finite group, no hyperbolic tiling.`;
      }
      apply.disabled = true;
      return;
    }
    status.dataset.kind = 'ok';
    const area = Math.PI * (1 - 1 / p - 1 / q - 1 / r);
    status.innerHTML = `Angle sum ${sum.toFixed(1)}° &lt; 180°: <em>hyperbolic</em> · area ${area.toFixed(4)}`;
    apply.disabled = same;
  }

  private updateStageLabel(): void {
    if (!this.app?.tg) return;
    const s = this.app.store.get();
    const model = MODELS.find((m) => m.id === s.model)!.label;
    const label = `${s.r === 2 ? `{${s.p},${s.q}}` : `(${s.p}, ${s.q}, ${s.r})`} tiling, ${
      s.rotation ? 'rotation group' : 'reflection group'
    }, ${model} model. Hold Space with the brush and use the arrow keys to draw, or use the arrow keys to travel.`;
    $('#overlay').setAttribute('aria-label', label);
  }

  /* ------------------------------------------------------------------ hint */

  private showHint(): void {
    const hint = $('#hint');
    hint.hidden = false;
    this.updateHintText();
  }

  private updateHintText(): void {
    const t = this.app?.store.get().tool;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const text = $('#hint-text');
    if (t === 'travel')
      text.textContent = `Drag to travel · ${coarse ? 'double-tap' : 'double-click'} to recentre`;
    else if (t === 'geodesic')
      text.textContent = `${coarse ? 'Tap' : 'Click'} two points to measure their distance`;
    else if (t === 'triangle')
      text.textContent = `${coarse ? 'Tap' : 'Click'} three points to make a triangle`;
    else if (t === 'circle')
      text.textContent = `${coarse ? 'Tap' : 'Click'} a centre, then a point on the circle`;
    else text.textContent = 'Paint inside the outlined tile — or drag to travel';
  }

  private hideHint(): void {
    const hint = $('#hint');
    if (hint.hidden) return;
    hint.classList.add('is-leaving');
    window.clearTimeout(this.hintTimer);
    this.hintTimer = window.setTimeout(() => {
      hint.hidden = true;
      hint.classList.remove('is-leaving');
    }, 520);
  }

  /* ------------------------------------------------------------------ notices, toasts */

  private renderNotice(n: NoticeSpec | null): void {
    const el = $('#notice');
    if (!n) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    el.hidden = false;
    el.dataset.kind = n.kind;
    el.innerHTML = n.html;
    const actions = n.actions ?? [];
    if (actions.length || n.dismissible) {
      const row = document.createElement('div');
      row.className = 'notice-actions';
      for (const a of actions) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'text-button';
        b.textContent = a.label;
        b.addEventListener('click', a.run);
        row.append(b);
      }
      if (n.dismissible) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'text-button';
        b.textContent = 'Dismiss';
        b.addEventListener('click', () => this.app.dropNotice(n.id));
        row.append(b);
      }
      el.append(row);
    }
  }

  toast(msg: string, kind: 'info' | 'error' = 'info'): void {
    const el = $('#toast');
    el.textContent = msg;
    el.dataset.kind = kind;
    el.hidden = false;
    // restart the entrance animation
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (el.hidden = true), 2600);
  }

  /* ------------------------------------------------------------------ readout */

  renderReadout(): void {
    if (!this.app) return;
    // wide layouts show the readout in the side panel so it never covers the picture
    const wide = WIDE.matches;
    const el = $(wide ? '#panel-readout' : '#readout');
    const other = $(wide ? '#readout' : '#panel-readout');
    other.hidden = true;
    other.innerHTML = '';
    const m = this.app.measurement;
    const tool = this.app.store.get().tool;
    const measuring = tool === 'geodesic' || tool === 'triangle' || tool === 'circle';
    if (!m || (!measuring && m.points.length === 0)) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const verb = coarse ? 'Tap' : 'Click';
    const ro = m.readout();
    let title: string;
    let rows: [label: string, value: string, testid?: string][] = [];
    let help: string;
    if (m.kind === 'geodesic') {
      title = 'Geodesic';
      if (ro?.kind === 'geodesic') {
        rows = [['Distance', fmt(ro.distance), 'distance']];
        help =
          'The shortest path is an arc that would meet the rim at right angles (dotted). Drag the ends.';
      } else {
        help = `${verb} ${m.points.length === 0 ? 'two points' : 'a second point'} on the plane, or press Enter to drop one at the centre.`;
      }
    } else if (m.kind === 'triangle') {
      title = 'Triangle';
      if (ro?.kind === 'triangle') {
        rows = [
          ['Angle sum', fmtDeg(ro.sum), 'angle-sum'],
          ['Angles', ro.angles.map(fmtDeg).join(' + ')],
          ['Area π − Σ', fmt(ro.area, 4)],
          ['Integrated', fmt(ro.integrated, 4)],
        ];
        help = 'Always under 180°: the missing angle is exactly the area (Gauss–Bonnet).';
      } else {
        const left = 3 - m.points.length;
        help = `${verb} ${left} more point${left === 1 ? '' : 's'}, or press Enter to drop one at the centre.`;
      }
    } else {
      title = 'Circle';
      if (ro?.kind === 'circle') {
        rows = [
          ['2π sinh r', fmt(ro.circumference), 'circumference'],
          ['Radius r', fmt(ro.radius)],
          ['Measured', fmt(ro.measured)],
          ['Flat 2πr', fmt(ro.euclidean)],
        ];
        help = 'The circumference grows exponentially with the radius.';
      } else {
        help = `${verb} ${m.points.length === 0 ? 'the centre' : 'a point on the circle'}.`;
      }
    }
    const clear =
      m.points.length > 0
        ? '<button type="button" class="text-button" id="readout-clear">Clear</button>'
        : '';
    const dl = rows.length
      ? `<dl>${rows
          .map(
            ([label, value, id], i) =>
              `<div class="row${i === 0 ? ' lead' : ''}"><dt>${label}</dt><dd${i === 0 ? ' class="big"' : ''}${id ? ` data-testid="${id}"` : ''}>${value}</dd></div>`,
          )
          .join('')}</dl>`
      : '';
    const html = `<div class="readout-head"><h3>${title}</h3>${clear}</div>${dl}<p class="readout-help${rows.length ? ' is-note' : ''}">${help}</p>`;
    el.innerHTML = html;
    el.hidden = false;
    el.querySelector('#readout-clear')?.addEventListener('click', () =>
      this.app.clearMeasurement(),
    );
  }

  /* ------------------------------------------------------------------ sheet, drawer, dialog */

  setSheet(open: boolean): void {
    const wasOpen = this.appEl.dataset.sheet === 'open';
    if (open === wasOpen) return;
    this.appEl.dataset.sheet = open ? 'open' : 'closed';
    $('#settings-toggle').setAttribute('aria-expanded', String(open));
    $('#scrim').hidden = !open;
    if (open) {
      this.lastFocus = document.activeElement as HTMLElement | null;
      window.setTimeout(() => {
        this.app.refreshEditor();
        $('#panel-close').focus();
      }, 60);
    } else this.lastFocus?.focus?.();
  }

  get sheetOpen(): boolean {
    return this.appEl.dataset.sheet === 'open';
  }

  async openDrawer(): Promise<void> {
    const drawer = $('#drawer');
    this.lastFocus = document.activeElement as HTMLElement | null;
    drawer.hidden = false;
    $('#scrim').hidden = false;
    $('#drawer-close').focus();
    if (!this.drawerLoaded) {
      try {
        const { renderHowItWorks } = await import('./drawer');
        await renderHowItWorks($('#drawer-body'));
        this.drawerLoaded = true;
      } catch (err) {
        $('#drawer-body').innerHTML =
          `<p>The explanation could not be loaded (${(err as Error).message}).</p>`;
        const b = document.createElement('button');
        b.className = 'text-button';
        b.type = 'button';
        b.textContent = 'Try again';
        b.addEventListener('click', () => void this.openDrawer());
        $('#drawer-body').append(b);
      }
    }
  }

  closeDrawer(): void {
    const drawer = $('#drawer');
    if (drawer.hidden) return;
    drawer.hidden = true;
    if (!this.sheetOpen) $('#scrim').hidden = true;
    this.lastFocus?.focus?.();
  }

  get drawerOpen(): boolean {
    return !$('#drawer').hidden;
  }

  showKeys(): void {
    const d = $<HTMLDialogElement>('#keys-dialog');
    if (!d.open) d.showModal();
  }

  /** Escape: close the topmost thing. Returns true if something was closed. */
  escape(): boolean {
    if (this.drawerOpen) {
      this.closeDrawer();
      return true;
    }
    if (this.sheetOpen) {
      this.setSheet(false);
      return true;
    }
    if (this.app.penDown) {
      this.app.setPen(false);
      return true;
    }
    if (this.app.measurement && this.app.measurement.points.length > 0) {
      this.app.clearMeasurement();
      return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ export + share */

  private async doExport(): Promise<void> {
    const btn = $<HTMLButtonElement>('#export');
    const progress = $<HTMLProgressElement>('#export-progress');
    let size = Number($<HTMLSelectElement>('#export-size').value);
    if (this.app.rendererKind === 'cpu' && size > 1024) {
      size = 1024;
      this.toast('The CPU preview exports at 1024 px');
    }
    const transparent = $<HTMLInputElement>('#export-transparent').checked;
    btn.disabled = true;
    progress.hidden = false;
    progress.value = 0;
    try {
      const blob = await this.app.exportPng(size, transparent, (f) => (progress.value = f));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.app.exportFilename();
      document.body.append(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
      this.toast(`Saved ${a.download} (${size} px)`);
    } catch (err) {
      this.toast(`Export failed: ${(err as Error).message}`, 'error');
    } finally {
      btn.disabled = false;
      progress.hidden = true;
    }
  }

  private async doShare(): Promise<void> {
    try {
      const link = await this.app.shareLink();
      let copied = false;
      try {
        await navigator.clipboard.writeText(link);
        copied = true;
      } catch {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.append(ta);
        ta.select();
        try {
          copied = document.execCommand('copy');
        } catch {
          copied = false;
        }
        ta.remove();
      }
      if (copied) this.toast(`Link copied — ${link.length.toLocaleString('en')} characters`);
      else window.prompt('Copy this link to share your motif:', link);
      document.documentElement.dataset.lastShare = link;
    } catch (err) {
      this.toast(`Could not make a link: ${(err as Error).message}`, 'error');
    }
  }
}

function tileWords(p: number, q: number): string {
  const names: Record<number, string> = {
    3: 'triangles',
    4: 'squares',
    5: 'pentagons',
    6: 'hexagons',
    7: 'heptagons',
    8: 'octagons',
  };
  const count: Record<number, string> = {
    3: 'three',
    4: 'four',
    5: 'five',
    6: 'six',
    7: 'seven',
    8: 'eight',
  };
  return `${names[p] ?? `${p}-gons`}, ${count[q] ?? q} at each vertex`;
}
